// Mock data for Paper Towel Dispensers based on real dispenser locations in Cary
// Usage patterns are determined by bathroom location type and crowd movement patterns

// Location types and their typical usage patterns
const LOCATION_TYPES = {
  BARK_BAR: { // Dog park area - high usage during peak dog walking times
    baseUsage: 0.7,
    peakHours: [7, 8, 17, 18, 19], // Morning and evening dog walks
    weekendMultiplier: 2.0,
    dailyUsageRate: 0.25 // 25% of capacity used per day on average
  },
  ACADEMY_PAVILION: { // Event/meeting space - variable usage based on events
    baseUsage: 0.5,
    peakHours: [10, 11, 12, 18, 19], // Event and meeting times
    weekendMultiplier: 1.5,
    dailyUsageRate: 0.15
  },
  DOWNTOWN_PARK_OFFICE: { // Administrative building - business hours usage
    baseUsage: 0.6,
    peakHours: [9, 10, 11, 13, 14, 15], // Business hours
    weekendMultiplier: 0.2,
    dailyUsageRate: 0.20
  },
  GATHERING_HOUSE: { // Community center - steady moderate usage
    baseUsage: 0.4,
    peakHours: [9, 12, 15, 18], // Program and activity times
    weekendMultiplier: 1.3,
    dailyUsageRate: 0.18
  },
  THE_NEST: { // Community facility - consistent usage
    baseUsage: 0.5,
    peakHours: [9, 12, 15, 18], // Program times
    weekendMultiplier: 1.2,
    dailyUsageRate: 0.20
  },
  PERFORMANCE_PAVILION: { // Event venue - high usage during events
    baseUsage: 0.3,
    peakHours: [19, 20, 21], // Evening performances
    weekendMultiplier: 2.5,
    dailyUsageRate: 0.12
  }
};

// Helper function to determine location type from dispenser name
const getLocationType = (dispenserName) => {
  const name = dispenserName.toLowerCase();
  if (name.includes('bark bar')) return LOCATION_TYPES.BARK_BAR;
  if (name.includes('academy pavilion')) return LOCATION_TYPES.ACADEMY_PAVILION;
  if (name.includes('downtown park') || name.includes('library office')) return LOCATION_TYPES.DOWNTOWN_PARK_OFFICE;
  if (name.includes('gathering house')) return LOCATION_TYPES.GATHERING_HOUSE;
  if (name.includes('the nest')) return LOCATION_TYPES.THE_NEST;
  if (name.includes('performance pavilion')) return LOCATION_TYPES.PERFORMANCE_PAVILION;
  return LOCATION_TYPES.GATHERING_HOUSE; // Default
};

// Helper function to calculate usage and fill level based on time and location
const calculateUsageMetrics = (locationType, dayOffset = 0, hourOffset = 0, isWeekend = false, seed = 0) => {
  const currentHour = (new Date().getHours() + hourOffset) % 24;
  const daysSinceLastRefill = Math.abs(dayOffset);
  
  let usageRate = locationType.baseUsage;
  
  // Add peak hour bonus
  if (locationType.peakHours.includes(currentHour)) {
    usageRate += 0.3;
  }
  
  // Weekend multiplier
  if (isWeekend) {
    usageRate *= locationType.weekendMultiplier;
  }
  
  // Add deterministic "randomness" based on seed
  const pseudoRandom1 = (Math.sin(seed * 1234) + 1) / 2;
  const pseudoRandom2 = (Math.sin(seed * 5678) + 1) / 2;
  
  usageRate += (pseudoRandom1 - 0.5) * 0.4; // Add some variation
  usageRate = Math.max(0.05, Math.min(1.0, usageRate)); // Clamp between 5% and 100%
  
  // Calculate current level (starts at 100%, decreases over time)
  let currentLevel = 100 - (daysSinceLastRefill * locationType.dailyUsageRate * 100);
  currentLevel -= usageRate * 15; // Current hour impact
  currentLevel = Math.max(0, Math.min(100, Math.round(currentLevel)));
  
  // Calculate daily usage (towels used)
  const dailyUsage = Math.round(usageRate * 50 + (pseudoRandom2 - 0.5) * 20); // 0-70 towels per day
  
  // Determine status
  let status = 'OK';
  if (currentLevel < 10) status = 'Empty';
  else if (currentLevel < 25) status = 'Low';
  
  return {
    currentLevel,
    dailyUsage,
    usageRate,
    status
  };
};

// Helper function to generate time series data for usage patterns
const generateUsageTimeSeries = (locationType, seed = 0) => {
  const timeSeries = [];
  const now = new Date();
  
  // Generate 7 days of usage data
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    let dailyUsage;
    if (i === 2) {
      // Dispenser was refilled 2 days ago
      dailyUsage = Math.floor((Math.sin((seed + i) * 7777) + 1) / 2 * 10) + 5; // 5-15 towels (low usage after refill)
    } else {
      const metrics = calculateUsageMetrics(locationType, -i, 0, isWeekend, seed + i);
      dailyUsage = metrics.dailyUsage;
    }
    
    timeSeries.push({
      day: i === 0 ? 'Today' : `Day -${i}`,
      used: dailyUsage,
      timestamp: date.toISOString()
    });
  }
  
  return timeSeries;
};

// Generate mock data for all paper towel dispensers
export const generatePaperTowelMockData = () => {
  const dispenserDefinitions = [
    { name: "Bark Bar Bathroom 306 Paper Towel Dispenser", mac: "62:c8:19:30:10:aa", lat: 35.7930425, lng: -78.7792443 },
    { name: "Academy Pavilion Bathroom Men's Paper Towel Dispenser", mac: "62:86:b1:da:44:a0", lat: 35.792168, lng: -78.7811254 },
    { name: "Downtown Park Library Office Men's Bathroom Paper Towel Dispenser", mac: "62:86:b1:da:44:a0", lat: 35.7917253, lng: -78.7803762 },
    { name: "Gathering House Bathroom 506 Paper Towel Dispenser", mac: "9a:01:82:4c:9a:fe", lat: 35.7929663, lng: -78.7805541 },
    { name: "Bark Bar Bathroom 308 Paper Towel Dispenser", mac: "76:fe:47:57:c5:bc", lat: 35.7930233, lng: -78.7792654 },
    { name: "Downtown Park Library Office Family Bathroom Paper Towel Dispenser", mac: "5e:95:f7:48:aa:d9", lat: 35.7916976, lng: -78.7803334 },
    { name: "The Nest Bathroom 402 Paper Towel Dispenser", mac: "42:41:55:c3:67:b4", lat: 35.7922407, lng: -78.7799948 },
    { name: "The Nest Bathroom 401 Paper Towel Dispenser", mac: "4e:a0:89:a2:81:21", lat: 35.7922365, lng: -78.7800195 },
    { name: "Gathering House Bathroom 505 Paper Towel Dispenser", mac: "a6:84:9c:55:ee:35", lat: 35.7930017, lng: -78.7805545 },
    { name: "Performance Pavilion Bathroom 204 Paper Towel Dispenser", mac: "dc:45:46:e2:bb:a7", lat: 35.7926895, lng: -78.7800269 },
    { name: "Academy Pavilion Bathroom Women's Paper Towel Dispenser", mac: "aa:eb:dc:07:bb:56", lat: 35.7922042, lng: -78.7811255 },
    { name: "Downtown Park Library Office Women's Bathroom Paper Towel Dispenser", mac: "a2:ef:08:03:d1:f4", lat: 35.7917003, lng: -78.7803738 },
    { name: "Bark Bar NonADA Bathroom Paper Towel Dispenser", mac: "90:9c:4a:c8:07:a1", lat: 35.7930269, lng: -78.7792479 }
  ];

  const dispensers = dispenserDefinitions.map((dispenserDef, index) => {
    const locationType = getLocationType(dispenserDef.name);
    const seed = index * 789; // Use index as seed for consistent data
    const currentMetrics = calculateUsageMetrics(locationType, 0, 0, false, seed);
    
    // Generate additional deterministic values
    const pseudoRandom1 = (Math.sin(seed * 1111) + 1) / 2;
    const pseudoRandom2 = (Math.sin(seed * 2222) + 1) / 2;
    const pseudoRandom3 = (Math.sin(seed * 3333) + 1) / 2;
    
    // Calculate refill schedule
    const daysSinceLastRefill = Math.floor(pseudoRandom1 * 5) + 1; // 1-6 days ago
    const lastRefilledAt = new Date(Date.now() - daysSinceLastRefill * 24 * 60 * 60 * 1000);
    
    // Next refill based on current level and usage rate
    const daysUntilRefill = currentMetrics.currentLevel < 25 ? 
      Math.floor(pseudoRandom2 * 2) + 1 : // 1-3 days if low
      Math.floor(pseudoRandom2 * 7) + 3; // 3-10 days if OK
    const nextRefillAt = new Date(Date.now() + daysUntilRefill * 24 * 60 * 60 * 1000);
    
    // Determine bathroom type and gender
    const bathroomType = dispenserDef.name.toLowerCase().includes('men') ? 'Men\'s' :
                        dispenserDef.name.toLowerCase().includes('women') ? 'Women\'s' :
                        dispenserDef.name.toLowerCase().includes('family') ? 'Family' :
                        dispenserDef.name.toLowerCase().includes('nonada') ? 'Accessible' : 'General';
    
    return {
      id: `PTD-${String(index + 1).padStart(3, '0')}`,
      name: dispenserDef.name,
      location: dispenserDef.name.split(' Paper Towel')[0], // Remove "Paper Towel Dispenser" from name
      macAddress: dispenserDef.mac,
      coordinates: {
        latitude: dispenserDef.lat,
        longitude: dispenserDef.lng
      },
      status: currentMetrics.status,
      currentLevelPercent: currentMetrics.currentLevel,
      lastRefilledAt: lastRefilledAt.toISOString(),
      nextRefillAt: nextRefillAt.toISOString(),
      bathroomType: bathroomType,
      locationType: Object.keys(LOCATION_TYPES).find(key => LOCATION_TYPES[key] === locationType),
      consumption7d: generateUsageTimeSeries(locationType, seed),
      // Additional metrics
      metrics: {
        dailyUsageAvg: Math.round(pseudoRandom1 * 30) + 10, // 10-40 towels per day average
        weeklyUsageAvg: Math.round(pseudoRandom2 * 200) + 100, // 100-300 towels per week
        refillFrequency: Math.round(pseudoRandom3 * 10) + 5, // 5-15 days between refills
        capacityTowels: 500, // Standard dispenser capacity
        currentTowels: Math.round(currentMetrics.currentLevel * 5), // Approximate towel count
        lastMaintenanceAt: new Date(Date.now() - Math.floor(pseudoRandom1 * 30) * 24 * 60 * 60 * 1000).toISOString(),
        batteryLevel: Math.floor(pseudoRandom2 * 30) + 70, // 70-100% (for smart dispensers)
        sensorStatus: pseudoRandom3 > 0.1 ? 'Online' : 'Offline'
      }
    };
  });

  return {
    dispensers: dispensers,
    summary: {
      totalDispensers: dispensers.length,
      needRefill: dispensers.filter(d => d.status === 'Low' || d.status === 'Empty').length,
      emptyDispensers: dispensers.filter(d => d.status === 'Empty').length,
      averageLevel: Math.round(dispensers.reduce((sum, d) => sum + d.currentLevelPercent, 0) / dispensers.length),
      totalDailyUsage: dispensers.reduce((sum, d) => sum + d.metrics.dailyUsageAvg, 0),
      lastUpdated: new Date().toISOString()
    }
  };
};

// Get dispenser data by name or MAC address
export const getDispenserByName = (dispenserName) => {
  const allData = generatePaperTowelMockData();
  return allData.dispensers.find(dispenser => 
    dispenser.name === dispenserName || dispenser.macAddress === dispenserName
  );
};

// Get dispensers by location type
export const getDispensersByLocation = (locationType) => {
  const allData = generatePaperTowelMockData();
  return allData.dispensers.filter(dispenser => dispenser.locationType === locationType);
};

// Get dispensers needing attention
export const getDispensersNeedingAttention = () => {
  const allData = generatePaperTowelMockData();
  return allData.dispensers.filter(dispenser => 
    dispenser.status === 'Low' || 
    dispenser.status === 'Empty' ||
    dispenser.metrics.sensorStatus === 'Offline'
  );
};

// Export default data for immediate use
export default generatePaperTowelMockData();
